import { useState } from "react";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDSnackbar from "components/MDSnackbar";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import httpClient from "api/httpClient";

export default function AgentTopup() {
  const [query, setQuery] = useState("");
  const [customer, setCustomer] = useState(null);
  const [wallet, setWallet] = useState(null);

  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);

  const [snack, setSnack] = useState({
    open: false,
    color: "success",
    content: "",
  });

  const openSnack = (color, content) => setSnack({ open: true, color, content });
  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  const findCustomer = async () => {
    setLoading(true);
    try {
      setCustomer(null);
      setWallet(null);
      const res = await httpClient.get(`/customers/resolve?q=${encodeURIComponent(query)}`);
      const first = res.data?.content;

      if (!first) {
        setCustomer(null);
        setWallet(null);
        openSnack("warning", "No customer found. Try card number or phone.");
        return;
      }

      setCustomer(first);

      const w = await httpClient.get(`/customers/${first.id}/wallet`);
      setWallet(w.data);
    } catch (e) {
      openSnack("error", e?.response?.data?.message || e.message || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  const initiate = async () => {
    if (!customer) {
      openSnack("warning", "Search/select a customer first");
      return;
    }
    if (!amount) {
      openSnack("warning", "Enter amount");
      return;
    }

    setLoading(true);
    try {
      const res = await httpClient.post("/transactions/topup/initiate", {
        customer_id: customer.id,
        amount_money: Number(amount),
      });
      openSnack("success", `Initiated: ${res.data?.internal_ref || "OK"}`);
    } catch (e) {
      openSnack("error", e?.response?.data?.message || e.message || "Topup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={3} pb={3}>
        <MDTypography variant="h4" mb={2}>
          Agent Top-up
        </MDTypography>

        <Card>
          <MDBox p={2} display="flex" gap={2} alignItems="center">
            <MDInput
              label="Search customer (card / phone / patient)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              fullWidth
            />
            <MDButton color="info" onClick={findCustomer} disabled={loading}>
              <Icon>search</Icon>&nbsp;Find
            </MDButton>
          </MDBox>

          {customer ? (
            <MDBox p={2} pt={0}>
              <MDTypography variant="button" fontWeight="bold">
                {customer.card_number} — {customer.owner_name || customer.client_name || "Customer"}
              </MDTypography>

              <MDBox alignItems="left" display="flex" gap={1} fontSize="0.75rem">
                Available:
                <MDTypography
                  variant="caption"
                  display="block"
                  fontWeight="bold"
                  fontSize="1.0rem"
                  color="success"
                >
                  {Number(wallet?.available_points || 0).toLocaleString()}
                </MDTypography>
                | Pending:
                <MDTypography
                  variant="caption"
                  display="block"
                  fontWeight="bold"
                  fontSize="1.0rem"
                  color="warning"
                >
                  {Number(wallet?.pending_points || 0).toLocaleString()}
                </MDTypography>
              </MDBox>
            </MDBox>
          ) : null}

          <MDBox p={2} pt={0} display="flex" gap={2} alignItems="center">
            <MDInput
              label="Amount (UGX)"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              sx={{ maxWidth: 240 }}
            />
            <MDButton color="success" onClick={initiate} disabled={loading}>
              <Icon>arrow_upward</Icon>&nbsp;Initiate Top-up
            </MDButton>
          </MDBox>

          <MDBox p={2} pt={0}>
            <MDTypography variant="caption" color="text">
              Note: This is a gateway placeholder. Plug your payment gateway into the backend and
              callback endpoint.
            </MDTypography>
          </MDBox>
        </Card>
      </MDBox>

      <MDSnackbar
        color={snack.color}
        icon="notifications"
        title="Loyalty System"
        content={snack.content}
        open={snack.open}
        onClose={closeSnack}
        close={closeSnack}
        bgWhite
      />
    </DashboardLayout>
  );
}
